import {selectOrCreateSession,mutateSession,type LocalSession} from './queue.ts';
import {supabase} from './supabase.ts';
import {withDeadline} from './requests.ts';
import type {Venue} from './venues.ts';
import type {RecordingLock} from './recordingLock.ts';

/** A durable intent is written BEFORE any server mutation. If the first write
    fails no request leaves. An uncertain request or failed acknowledgement
    write leaves this same intent available for retry, including after reload. */
export async function prepareStart(venue:Venue,observerId:string,recording:RecordingLock):Promise<LocalSession> {
  recording.assert();
  return selectOrCreateSession(sessions=>{
    // This runs after the asynchronous read, inside its write transaction.
    // No ID allocation or put may follow a read delivered to a former owner.
    recording.assert();
    const unfinished=sessions.filter(s=>s.observerId===observerId && s.endedAt===null);
    const existing=unfinished.find(s=>s.venueId===venue.id);
    if(existing)return existing;
    if(unfinished.length)throw new Error('Resume or end the unfinished session before starting at another venue.');
    const now=Date.now();
    return {id:crypto.randomUUID(),venueId:venue.id,waveId:crypto.randomUUID(),observerId,venueName:venue.name,
      cleanerRef:null,startedAt:now,endedAt:null,startPending:true,history:[],historyComplete:true,
      pendingBlocks:[],pendingTaps:[],pendingNotes:[],lastSyncAt:null,lastError:null,nextSeq:1,lastSeenAt:now,endSyncedAt:null};
  });
}
export async function confirmStart(s:LocalSession,observerId:string,recording:RecordingLock):Promise<LocalSession> {
  recording.assert();
  if(s.observerId!==observerId)throw new Error('Sign in with the account that started this session.');
  if(!s.startPending)return s;
  const r=await withDeadline(deadline=>{
    recording.assert();
    // Auth/token acquisition may defer the actual fetch. Losing ownership
    // cancels that unsent request too; an already-sent request may have landed,
    // so its durable identity is retained for the next owner's safe retry.
    const stopped=new AbortController();
    const cancel=()=>stopped.abort();
    const sources=[deadline,recording.signal];
    for(const source of sources) {
      source.addEventListener('abort',cancel,{once:true});
      if(source.aborted)cancel();
    }
    return Promise.resolve(supabase.rpc('start_observation_session',{
      p_id:s.id,p_wave_id:s.waveId,p_venue_id:s.venueId,p_started_at:s.startedAt,
    }).abortSignal(stopped.signal)).finally(()=>{
      for(const source of sources)source.removeEventListener('abort',cancel);
    });
  },30_000);
  if(r.error)throw r.error;
  const row=r.data;
  if(row?.id!==s.id||row.wave_id!==s.waveId||row.observer_id!==observerId||row.started_at!==s.startedAt) throw new Error('The session start could not be verified. Retry the same start.');
  if(row.ended_at!==null||row.voided_at!==null)throw new Error('This session is already ended. Review it in Sessions before starting another.');
  const stored=await mutateSession(s.id,null,current=>{
    recording.assert();
    return {...current,startPending:false,lastError:null};
  });
  if(!stored)throw new Error('The start could not be saved on this phone. Retry without clearing its storage.');
  return stored;
}
